import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Zap, MailCheck, RefreshCw } from "lucide-react";
import { useAuth } from "../lib/auth";
import Brand from "../components/Brand";

export default function Login() {
  const { user, ready, login, resendVerification } = useAuth();
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resending, setResending] = useState(false);
  const [verifiedBanner, setVerifiedBanner] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  // Read ?verified=1 / ?verified=0 once and strip it from the URL so the
  // banner doesn't reappear on subsequent renders.
  useEffect(() => {
    const v = params.get("verified");
    if (v === "1") setVerifiedBanner("Email verified — you can now sign in.");
    else if (v === "0") {
      const reason = params.get("reason");
      setErr(
        reason === "invalid"
          ? "That verification link is invalid or has already been used. Try signing in, or request a new link."
          : "Verification link was missing a token. Please request a new one.",
      );
    }
    if (v !== null) {
      const next = new URLSearchParams(params);
      next.delete("verified");
      next.delete("reason");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ready && user && user.id) return <Navigate to="/projects" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setUnverifiedEmail(""); setResendMsg("");
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) { nav("/projects"); return; }
    if (r.code === "email_not_verified") {
      setUnverifiedEmail(r.email || email.trim());
      setErr(r.error || "Please verify your email before signing in.");
      return;
    }
    setErr(r.error || "Login failed");
  };

  const doResend = async () => {
    if (!unverifiedEmail) return;
    setResending(true); setResendMsg("");
    const r = await resendVerification(unverifiedEmail);
    setResending(false);
    setResendMsg(r.message || (r.ok ? "Verification email sent." : r.error || "Could not resend."));
  };

  return (
    <div className="pf-blueprint-bg" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div className="pf-card pf-grain" style={{ width: 440, padding: 32 }}>
        <Link to="/" className="pf-heading" style={{ display: "inline-flex", alignItems: "center", color: "var(--pf-text)", textDecoration: "none", marginBottom: 22 }}>
          <Brand height={84} withTagline testId="login-brand" />
        </Link>

        {verifiedBanner && (
          <div
            role="status"
            data-testid="login-verified-banner"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", marginBottom: 16,
              background: "rgba(14,124,107,0.12)",
              border: "1px solid rgba(14,124,107,0.45)",
              color: "var(--pf-ok)",
              fontSize: 13, borderRadius: 2,
            }}
          >
            <MailCheck size={15} /> {verifiedBanner}
          </div>
        )}

        <div className="pf-overline" style={{ marginBottom: 6 }}>ACCESS · AUTHENTICATE</div>
        <h1 className="pf-heading" style={{ fontSize: 28, margin: "0 0 22px" }}>Sign in to your workbench</h1>

        <form onSubmit={submit}>
          <label className="pf-label" htmlFor="li-email">EMAIL</label>
          <input id="li-email" className="pf-input" type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email" />

          <div style={{ height: 14 }} />

          <label className="pf-label" htmlFor="li-password">PASSWORD</label>
          <input id="li-password" className="pf-input" type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" />
          <div style={{ marginTop: 6, textAlign: "right" }}>
            <Link to="/forgot-password" className="pf-link" style={{ fontSize: 12 }} data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>

          {err && (
            <div className="pf-sev pf-sev-error" style={{ display: "block", marginTop: 14, padding: "8px 10px" }} data-testid="login-error">
              {err}
            </div>
          )}

          {unverifiedEmail && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 2 }} data-testid="login-resend-box">
              <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)", marginBottom: 8 }}>
                Didn't get the email?
              </div>
              <button type="button" className="btn btn-ghost" onClick={doResend} disabled={resending} data-testid="login-resend-btn">
                {resending ? <span className="pf-spin" /> : <RefreshCw size={13} />} Resend verification to {unverifiedEmail}
              </button>
              {resendMsg && (
                <div className="pf-sev pf-sev-info" style={{ display: "block", marginTop: 10, padding: "8px 10px" }} data-testid="login-resend-msg">{resendMsg}</div>
              )}
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ marginTop: 20, width: "100%" }} disabled={busy} data-testid="login-submit">
            {busy ? <><span className="pf-spin" /> Authenticating…</> : <><Zap size={14} /> Sign in <ArrowRight size={14} /></>}
          </button>
        </form>

        <div style={{ marginTop: 18, fontSize: 13, color: "var(--pf-text-muted)" }}>
          No account? <Link to="/register" className="pf-link" data-testid="login-to-register">Create one</Link>
        </div>
      </div>
    </div>
  );
}
